import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import * as Components from "us.common/components";
import {
  VAT_CODE_TABLE_COLUMNS,
  VatCodeTableColumn,
} from "us.collection.economy/components/VATCode/Components/Table/Constants";
import { MoreOutlined } from "us.icons";
import { ItemMenu } from "./Components";
import { connect, ConnectedProps } from "react-redux";
import { getColumnWidth, getColumnFilterProps } from "./Functions";
import { IVatCodeTable, IItemMenu } from "./Interfaces";
import { IRootState } from "us.collection/interfaces";
import { VatCodeDetails } from "us.collection.economy/interfaces";
import {
  ITEM_MENU_DEFAULT_STATE,
  VatCodeAction,
} from "us.collection.economy/components/VATCode/Constants";

const { $Button, $DateLabel, $Popover, $TableTree } = Components;

/**
 * @description - View Vat Code List in the Table view
 * @link Design Document - https://unicorn-solutions.atlassian.net/wiki/spaces/USU/pages/2855108613/VAT+Code
 * @author Kaushalya Sandamali <kaushalyasa@unicorn-solutions.com>
 * @since 28/07/2022
 */
const Table: React.FC<IVatCodeTable & PropsFromRedux> = (props) => {
  const { onCallAction, vatCodeList } = props;
  const { t } = useTranslation();
  const [itemMenu, setItemMenu] = useState<IItemMenu>(ITEM_MENU_DEFAULT_STATE);

  // auto close vatcode item menu after 5 seconds
  const setAutoClose = () => {
    setTimeout(() => {
      setItemMenu(ITEM_MENU_DEFAULT_STATE);
    }, 5000);
  };

  /**
   * @description - Generate table columns with sorter and filters
   * @returns - An array of columns
   */
  const getColumns = (): any[] => {
    const columns: any[] = [];
    VAT_CODE_TABLE_COLUMNS.map(({ key, title }, _index) => {
      let column: any = {
        dataIndex: key,
        key,
        width: getColumnWidth(key),
        title: title && t(`US.COLLECTION.ECONOMY:VAT_CODE.${title}`),
      };
      if (key !== VatCodeTableColumn.MENU) {
        column = {
          ...column,
          customSorter: (a: any, b: any) => {
            if (
              [
                VatCodeTableColumn.NAME,
                VatCodeTableColumn.DESCRIPTION,
                VatCodeTableColumn.MODIFIED_USER,
              ].includes(key)
            ) {
              return a?.localeCompare(b);
            } else if (key === VatCodeTableColumn.START_DATE) {
              return new Date(a).getTime() - new Date(b).getTime();
            } else {
              return a - b;
            }
          },
        };
      }
      if (key === VatCodeTableColumn.MENU) {
        column = {
          ...column,
          customRenderChild: (text: any, record: VatCodeDetails) => {
            return (
              <$Popover
                placement="bottomLeft"
                trigger="click"
                open={itemMenu.visible && itemMenu.id === record.vatId}
                onOpenChange={(visible: boolean) => {
                  setItemMenu({
                    ...itemMenu,
                    visible,
                    id: record.vatId,
                  });
                  setAutoClose();
                }}
                content={
                  <ItemMenu
                    onCallAction={(actionType: VatCodeAction) => {
                      setItemMenu(ITEM_MENU_DEFAULT_STATE);
                      onCallAction(record, actionType);
                    }}
                  />
                }
                destroyTooltipOnHide
              >
                <$Button
                  data-testid="popover-btn"
                  id="btnItemMenu"
                  type="default"
                  icon={<MoreOutlined />}
                  size="small"
                />
              </$Popover>
            );
          },
        };
      } else if (key === VatCodeTableColumn.START_DATE) {
        column = {
          ...column,
          customRenderChild: (text: any, record: VatCodeDetails) => {
            return (
              <div className="d-flex align-items-center">
                {record.startDate && <$DateLabel value={record.startDate} />}
              </div>
            );
          },
          ...getColumnFilterProps(key),
        };
      } else {
        column = {
          ...column,
          customFilter: true,
          showFilters: true,
          customRenderChild: (text: any, record: any) => {
            return (
              <span>
                {key === VatCodeTableColumn.RATE ? text.toFixed(2) : text}
              </span>
            );
          },
          ...getColumnFilterProps(key),
        };
      }
      columns.push(column);
    });
    return columns;
  };

  return (
    <$TableTree
      data-testid="vatCode-table"
      rowKey={(record: VatCodeDetails) => record.vatId}
      data={vatCodeList}
      size="small"
      className="mt-3 table-striped header-custom-tag"
      onSort={(sortData, dataSource) => {
        return sortData(dataSource);
      }}
      onFilter={(searchData, dataSource) => {
        return searchData(dataSource);
      }}
      filterOnType
      resetOnSourceChange
      bordered
      showResetAllFilters
      pagination={{
        defaultPageSize: 20,
      }}
      scroll={{
        x: 900,
        y: "calc(100vh - 265px)",
      }}
      columns={getColumns()}
      onRow={(record, _rowIndex) => {
        return {
          onDoubleClick: (_event) => {
            onCallAction(record, VatCodeAction.EDIT);
          },
        };
      }}
    />
  );
};

const mapStateToProps = (state: IRootState) => {
  const { vatCode } = state;
  const { list } = vatCode;

  return {
    vatCodeList: list,
  };
};

const mapDispatchToProps = {};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(Table);
